import { ApiError } from '../errors';
import { ApiTransport, type SessionAdapter } from '../transport';

/**
 * The session coordinator.
 *
 * One per frontend. It owns the access token, the refresh credential and the
 * decision about when a session is over — and it owns them in one place so that
 * "am I signed in" has a single answer rather than one per screen.
 *
 * The access token lives in memory only, on both platforms. It is short-lived
 * and re-obtainable, and keeping it out of persistence removes a whole class of
 * exfiltration. What persists is the refresh credential, and *where* it
 * persists is the one genuine platform difference:
 *
 *   - **Mobile** stores it in Expo Secure Store, which is the keychain.
 *   - **Web** does not store it at all. The server sets an HttpOnly cookie the
 *     browser will not let script read, so there is nothing here to keep.
 *
 * That difference is expressed as a `RefreshCredentialStore` rather than a
 * branch, so neither app carries the other's assumptions.
 */

export interface AuthenticatedUser {
  id: string;
  email: string;
  displayName: string;
  organizationId: string;
  employeeId: string | null;
  roles: string[];
  permissions: string[];
}

export interface SignInResult {
  accessToken: string;
  refreshToken: string | null;
  expiresIn: number;
  mustChangePassword: boolean;
  user: AuthenticatedUser;
}

/**
 * Where the refresh credential lives between launches.
 *
 * The web implementation is deliberately a no-op: the credential is in a cookie
 * the browser holds and script cannot read, which is the point.
 */
export interface RefreshCredentialStore {
  read(): Promise<string | null>;
  write(token: string | null): Promise<void>;
  clear(): Promise<void>;
}

export const noRefreshStore: RefreshCredentialStore = {
  async read() {
    return null;
  },
  async write() {
    /* Held by the browser as an HttpOnly cookie. Nothing to do. */
  },
  async clear() {
    /* Cleared by the server on logout, which is the only thing that can. */
  },
};

/** What the server's sign-in screens may offer (GET /auth/options). */
export interface SignInOptions {
  /** False in production: no password field at all. */
  passwordLogin: boolean;
  magicLink: boolean;
  /** False until Google is configured on the server: no Google button. */
  google: boolean;
}

export type SessionState =
  | { status: 'unknown' }
  | { status: 'signed_out'; reason?: SignOutReason; message?: string }
  | { status: 'signed_in'; user: AuthenticatedUser; mustChangePassword: boolean };

export type SignOutReason =
  | 'requested'
  | 'expired'
  | 'revoked'
  | 'account_inactive'
  | 'refresh_failed'
  /** The server refused a refresh after the organisation's idle timeout (web sessions). */
  | 'idle_timeout';

export interface SessionCoordinatorOptions {
  baseUrl: string;
  /**
   * Which kind of client this is, sent in the login and refresh bodies.
   *
   * The server decides where the refresh credential travels from this field:
   * a web client gets an HttpOnly cookie and no token in the body, a mobile
   * client gets the token in the body for the keychain. It was omitted, and
   * the omission was quietly catastrophic for exactly one platform — the
   * server fell through to the mobile behaviour, the web's no-op store threw
   * the body token away, and no cookie was ever set. Sign-in worked; the
   * session then failed to survive its first full page load, which is the
   * kind of bug that reads as "sometimes I get logged out" for as long as
   * nobody suspects login itself.
   */
  client: 'web' | 'mobile';
  store?: RefreshCredentialStore;
  fetch?: typeof globalThis.fetch;
  defaultHeaders?: () => Record<string, string>;
  /** Device id sent at sign-in, so the server can name the session. */
  deviceId?: () => string | undefined;
  onStateChange?: (state: SessionState) => void;
  /** Called when a session ends so caches and sockets can be torn down. */
  onSignOut?: (reason: SignOutReason, message?: string) => void | Promise<void>;
  /** Seconds before expiry to renew proactively. */
  refreshSkewSeconds?: number;
}

export class SessionCoordinator {
  readonly transport: ApiTransport;

  private accessToken: string | null = null;
  private user: AuthenticatedUser | null = null;
  private expiresAt = 0;
  private mustChangePassword = false;
  private renewalTimer: ReturnType<typeof setTimeout> | null = null;
  private state: SessionState = { status: 'unknown' };

  /**
   * The refresh in flight, if any.
   *
   * The transport already collapses concurrent 401s, but that guard sits below
   * this class and does not cover a caller invoking `refresh` or `restore`
   * twice. React StrictMode does exactly that in development — it runs effects
   * twice — and the second call presents a refresh token the first has already
   * rotated. The server reads that as theft, correctly revokes the session, and
   * the person is signed out on every page load.
   *
   * A rotating credential means refresh can never be re-entrant, wherever the
   * second call comes from.
   */
  private refreshInFlight: Promise<string | null> | null = null;

  /**
   * Why the last refresh was refused, when the server said something more
   * specific than "expired" — so the sign-out it leads to can say so.
   */
  private refreshFailure: { reason: SignOutReason; message: string } | null = null;

  private readonly options: SessionCoordinatorOptions;
  private readonly store: RefreshCredentialStore;

  constructor(options: SessionCoordinatorOptions) {
    this.options = options;
    this.store = options.store ?? noRefreshStore;

    const adapter: SessionAdapter = {
      getAccessToken: () => this.accessToken,
      refresh: () => this.refresh(),
      onUnauthorized: () => this.endSession('expired'),
    };

    this.transport = new ApiTransport({
      baseUrl: options.baseUrl,
      session: adapter,
      fetch: options.fetch,
      defaultHeaders: options.defaultHeaders,
    });
  }

  getState(): SessionState {
    return this.state;
  }

  /**
   * The current access token, for a caller that cannot go through `transport`.
   *
   * The WebSocket is the only one: it authenticates in its handshake rather
   * than per request, so it needs the token itself. Read fresh on every
   * connect — a reconnect after a rotation must present the new token, and a
   * captured one is stale exactly when the socket needs it most.
   *
   * Deliberately not a general accessor for feature code. Anything making HTTP
   * requests goes through `transport`, which attaches the header, refreshes on
   * 401 and retries once; a caller holding the raw token does none of that.
   */
  getAccessTokenForSocket(): string | null {
    return this.accessToken;
  }

  getUser(): AuthenticatedUser | null {
    return this.user;
  }

  isSignedIn(): boolean {
    return this.state.status === 'signed_in';
  }

  async signIn(email: string, password: string): Promise<SignInResult> {
    const result = await this.transport.post<SignInResult>(
      '/api/v1/auth/login',
      { email, password, client: this.options.client, deviceId: this.options.deviceId?.() },
      { anonymous: true },
    );

    await this.adopt(result);
    return result;
  }

  /** What the sign-in screen should offer. Asked before it is drawn. */
  async signInOptions(): Promise<SignInOptions> {
    return this.transport.get<SignInOptions>('/api/v1/auth/options', { anonymous: true });
  }

  /**
   * Ask for an emailed sign-in link.
   *
   * Resolves with the server's sentence, which is the same whether or not the
   * address is registered — and should be shown as it is.
   *
   * `redirect` is the app's own callback address, for an app whose scheme is
   * not the default (Expo Go in development); the server ignores anything it
   * does not allow.
   */
  async requestSignInLink(email: string, redirect?: string): Promise<{ message: string }> {
    return this.transport.post<{ message: string }>(
      '/api/v1/auth/magic-link',
      { email, client: this.options.client, ...(redirect ? { redirect } : {}) },
      { anonymous: true },
    );
  }

  /** Finish signing in with the token from an emailed link (the console's /auth/magic). */
  async signInWithLink(token: string): Promise<SignInResult> {
    const result = await this.transport.post<SignInResult>(
      '/api/v1/auth/magic-link/verify',
      { token, client: this.options.client, deviceId: this.options.deviceId?.() },
      { anonymous: true },
    );
    await this.adopt(result);
    return result;
  }

  /**
   * Finish a sign-in that happened in the browser — an emailed link or
   * Google — with the one-time code the browser handed back to the app.
   */
  async signInWithCode(code: string): Promise<SignInResult> {
    const result = await this.transport.post<SignInResult>(
      '/api/v1/auth/exchange',
      { code, deviceId: this.options.deviceId?.() },
      { anonymous: true },
    );
    await this.adopt(result);
    return result;
  }

  /** Where a browser goes to begin signing in with Google. */
  googleSignInUrl(redirect?: string): string {
    const params = new URLSearchParams({ client: this.options.client });
    if (redirect) params.set('redirect', redirect);
    return `${this.options.baseUrl.replace(/\/$/, '')}/api/v1/auth/google/start?${params.toString()}`;
  }

  /**
   * Restore a session at launch.
   *
   * Returns true when the person is still signed in. A failure here is
   * ordinary — an expired refresh token is what happens after a weekend — so
   * it resolves false rather than throwing.
   */
  async restore(): Promise<boolean> {
    const renewed = await this.refresh();
    if (!renewed) {
      this.setState(
        this.refreshFailure
          ? { status: 'signed_out', ...this.refreshFailure }
          : { status: 'signed_out' },
      );
      return false;
    }
    return true;
  }

  /**
   * Exchange the refresh credential for a new access token.
   *
   * Called by the transport on a 401 and by the renewal timer before expiry.
   * The transport guarantees only one runs at a time.
   */
  async refresh(): Promise<string | null> {
    // Everybody who arrives while one is running awaits the same result. The
    // token rotates exactly once.
    this.refreshInFlight ??= this.performRefresh().finally(() => {
      this.refreshInFlight = null;
    });

    return this.refreshInFlight;
  }

  private async performRefresh(): Promise<string | null> {
    const stored = await this.store.read();

    try {
      const result = await this.transport.post<SignInResult>(
        '/api/v1/auth/refresh',
        // The web client sends nothing: its credential is the cookie, and the
        // server reads it from there when the body omits a token.
        stored
          ? { refreshToken: stored, client: this.options.client }
          : { client: this.options.client },
        { anonymous: true },
      );

      await this.adopt(result);
      return result.accessToken;
    } catch (error: unknown) {
      // Expired, revoked, or reuse detected. All three mean the same thing to
      // the caller and none of them is worth an exception. An idle timeout is
      // remembered, so the sign-out can tell the person why.
      this.refreshFailure =
        error instanceof ApiError && error.code === 'SESSION_IDLE_TIMEOUT'
          ? { reason: 'idle_timeout', message: error.serverMessage }
          : null;
      await this.store.clear();
      return null;
    }
  }

  async signOut(everywhere = false): Promise<void> {
    try {
      await this.transport.post(everywhere ? '/api/v1/auth/logout-all' : '/api/v1/auth/logout', {});
    } catch {
      // A logout that cannot reach the server still clears this device. The
      // alternative — refusing to sign out while offline — is worse.
    }
    await this.endSession('requested');
  }

  /** Re-read identity and permissions without a full sign-in. */
  async reloadIdentity(): Promise<AuthenticatedUser | null> {
    if (!this.accessToken) return null;

    const me = await this.transport.get<{
      user: {
        id: string;
        email: string;
        displayName: string;
        organizationId: string;
        employeeId: string | null;
      };
      roles: string[];
      permissions: string[];
    }>('/api/v1/auth/me');

    this.user = { ...me.user, roles: me.roles, permissions: me.permissions };
    this.setState({
      status: 'signed_in',
      user: this.user,
      mustChangePassword: this.mustChangePassword,
    });
    return this.user;
  }

  private async adopt(result: SignInResult): Promise<void> {
    this.refreshFailure = null;
    this.accessToken = result.accessToken;
    this.user = result.user;
    this.mustChangePassword = result.mustChangePassword;
    this.expiresAt = Date.now() + result.expiresIn * 1000;

    // Null on web: the server put it in a cookie and there is nothing to keep.
    if (result.refreshToken) await this.store.write(result.refreshToken);

    this.scheduleRenewal(result.expiresIn);
    this.setState({
      status: 'signed_in',
      user: result.user,
      mustChangePassword: result.mustChangePassword,
    });
  }

  /**
   * Renew before expiry rather than after a failure.
   *
   * Waiting for a 401 means one request per session pays a round trip it did
   * not have to, and a mutation is as likely to be the one that pays it as a
   * read — so an approval a person clicked would stall behind a token refresh.
   */
  private scheduleRenewal(expiresInSeconds: number): void {
    this.clearRenewal();

    const skew = this.options.refreshSkewSeconds ?? 60;
    const delayMs = Math.max(expiresInSeconds - skew, 10) * 1000;

    this.renewalTimer = setTimeout(() => {
      void this.refresh().then((token) => {
        if (!token) void this.endSession('expired');
      });
    }, delayMs);

    // Never hold a Node process open on this alone; harmless in a browser.
    (this.renewalTimer as { unref?: () => void }).unref?.();
  }

  private clearRenewal(): void {
    if (this.renewalTimer) clearTimeout(this.renewalTimer);
    this.renewalTimer = null;
  }

  private async endSession(requested: SignOutReason): Promise<void> {
    // A session ended by a refused refresh ends for the reason it was refused.
    const failure = requested === 'expired' ? this.refreshFailure : null;
    const reason = failure?.reason ?? requested;
    this.clearRenewal();
    this.accessToken = null;
    this.user = null;
    this.expiresAt = 0;
    await this.store.clear();
    this.setState(
      failure
        ? { status: 'signed_out', reason, message: failure.message }
        : { status: 'signed_out', reason },
    );
    await this.options.onSignOut?.(reason, failure?.message);
  }

  private setState(next: SessionState): void {
    this.state = next;
    this.options.onStateChange?.(next);
  }

  /** Seconds until the access token expires. Diagnostics only. */
  get secondsUntilExpiry(): number {
    if (!this.expiresAt) return 0;
    return Math.max(0, Math.round((this.expiresAt - Date.now()) / 1000));
  }
}
